import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import tenantInfoService from '@/services/tenantInfoService';

const DEFAULT_LAT = 41.0082;
const DEFAULT_LNG = 28.9784;

/**
 * Composable for managing address save/load operations
 */
export function useAddressManagement(profile, emit) {
  const store = useStore();
  const { t } = useI18n();
  const isSaving = ref(false);

  const originalAddress = ref(profile.value?.address || '');
  const address = ref(profile.value?.address || '');
  const currentLocation = ref({
    lat: profile.value?.latitude || DEFAULT_LAT,
    lng: profile.value?.longitude || DEFAULT_LNG
  });

  const addressChanged = computed(() => {
    return address.value !== originalAddress.value ||
           currentLocation.value.lat !== (profile.value?.latitude || DEFAULT_LAT) ||
           currentLocation.value.lng !== (profile.value?.longitude || DEFAULT_LNG);
  });

  const loadAddressFromApi = async () => {
    try {
      const geoLocationData = await tenantInfoService.getGeoLocation();

      if (geoLocationData) {
        address.value = geoLocationData.display_name || '';
        originalAddress.value = geoLocationData.display_name || '';

        if (geoLocationData.lat && geoLocationData.lon) {
          currentLocation.value = {
            lat: parseFloat(geoLocationData.lat),
            lng: parseFloat(geoLocationData.lon)
          };
        }
      }
    } catch (error) {
      console.error('Error loading address from API:', error);
    }
  };

  const saveAddress = async (updateMapLocation) => {
    if (!addressChanged.value) {
      return;
    }

    isSaving.value = true;

    try {
      const addressUpdate = {
        address: address.value,
        latitude: currentLocation.value.lat,
        longitude: currentLocation.value.lng
      };

      const geoLocationData = {
        place_id: 0,
        licence: '',
        osm_type: '',
        osm_id: 0,
        lat: currentLocation.value.lat.toString(),
        lon: currentLocation.value.lng.toString(),
        class: '',
        type: '',
        place_rank: 0,
        importance: 0,
        addresstype: '',
        name: '',
        display_name: address.value,
        address: null,
        boundingBox: null
      };

      await tenantInfoService.saveGeoLocation(geoLocationData);
      emit('update:address', addressUpdate);
      originalAddress.value = address.value;

      if (updateMapLocation) {
        updateMapLocation(currentLocation.value.lat, currentLocation.value.lng);
      }

      store.dispatch('notify', {
        message: t('profile.map.addressSaved'),
        type: 'success'
      });
    } catch (error) {
      console.error('Error saving address:', error);
      store.dispatch('notify', {
        message: t('profile.map.addressSaveError'),
        type: 'error'
      });
    } finally {
      isSaving.value = false;
    }
  };

  const updateAddress = (newAddress) => {
    address.value = newAddress;
  };

  const updateLocation = (lat, lng) => {
    currentLocation.value = { lat, lng };
  };

  const resetOriginalAddress = () => {
    originalAddress.value = address.value;
  };

  return {
    address,
    originalAddress,
    currentLocation,
    isSaving,
    addressChanged,
    loadAddressFromApi,
    saveAddress,
    updateAddress,
    updateLocation,
    resetOriginalAddress
  };
}

